import clsx from 'clsx';
import type { CSSProperties } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import type { DrupalBlock, DrupalImage as DrupalImageType } from '@commons/types/drupal';
import type { Breakpoint } from '@commons/types/styles';

import styles from './index.module.scss';

// Type definition for the ResponsiveImageBlock component properties.

type Props = DrupalBlock<{
  desktopImage?: DrupalImageType;
  mobileImage?: DrupalImageType;
  mobileBreakpoint?: Breakpoint;
}>;

/**
 * Returns the shared inline styles for an image variant.
 */

const getImageStyles = (image: DrupalImageType): CSSProperties => ({
  aspectRatio: `${image.width} / ${image.height}`,
});

/**
 * Responsive image inline block component.
 *
 * Renders separate desktop and mobile images and switches between them at a configurable breakpoint.
 */

const ResponsiveImageBlock = ({ content }: Props) => {
  const { desktopImage, mobileImage, mobileBreakpoint = 'md' } = content;

  // Fallbacks in case Drupal does not return both images.
  if (!desktopImage?.url && !mobileImage?.url) return null;

  const desktopVariant = desktopImage?.url ? desktopImage : mobileImage;
  if (!desktopVariant?.url) return null;

  const hasMobileImage = Boolean(desktopImage?.url && mobileImage?.url);

  const wrapperClasses = clsx(
    styles.responsiveImage,
    hasMobileImage && styles.responsive,
    hasMobileImage && styles[mobileBreakpoint]
  );

  return (
    <div className={wrapperClasses}>
      <DrupalImage
        {...desktopVariant}
        className={styles.desktopImage}
        style={getImageStyles(desktopVariant)}
      />
      {hasMobileImage && mobileImage && (
        <DrupalImage
          {...mobileImage}
          className={styles.mobileImage}
          style={getImageStyles(mobileImage)}
        />
      )}
    </div>
  );
};

export default ResponsiveImageBlock;
